import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {Page, Pageable} from '../../../model/rest/base';
import {
  LearningUnitTeacherStatus,
  TeacherStats,
  V3CourseTeacherProfile,
  V3CourseTeacherProfileDetails,
  V3Teacher,
  V3TeacherProfile,
} from '../../../model/v3/schools';
import {VerifiedPasswordChangeRequest} from '../../../model/v3/people';
import {appendIfDefined, createPageableParams, map404ToNull, serializeDateInput,} from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class TeacherV3RestService {
  constructor(private http: HttpClient) {
  }

  private url(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/teachers${path}`;
  }

  getTeacher(schoolId: number, teacherId: number): Observable<V3Teacher> {
    return this.http.get<V3Teacher>(this.url(schoolId, `/${teacherId}`));
  }

  findTeachers(
    schoolId: number,
    term?: string | null,
    productCode?: string | null,
    pageable?: Pageable,
  ): Observable<Page<V3Teacher>> {
    let params = createPageableParams(pageable);
    const normalizedTerm = term?.trim();
    params = appendIfDefined(params, 'term', normalizedTerm ? normalizedTerm : undefined);
    params = appendIfDefined(params, 'productCode', productCode);
    return this.http.get<Page<V3Teacher>>(this.url(schoolId), {params});
  }

  countTeacherStats(
    schoolId: number,
    dateFrom?: Date | string | null,
    dateTo?: Date | string | null,
  ): Observable<TeacherStats> {
    let params = appendIfDefined(createPageableParams(undefined), 'dateFrom', serializeDateInput(dateFrom));
    params = appendIfDefined(params, 'dateTo', serializeDateInput(dateTo));
    return this.http.get<TeacherStats>(this.url(schoolId, '/stats'), {params});
  }

  deleteTeacher(schoolId: number, teacherId: number): Observable<void> {
    return this.http.delete<void>(this.url(schoolId, `/${teacherId}`));
  }

  getTeacherProfile(
    schoolId: number,
    teacherId: number,
  ): Observable<V3TeacherProfile | null> {
    return this.http
      .get<V3TeacherProfile>(this.url(schoolId, `/${teacherId}/profile`))
      .pipe(map404ToNull<V3TeacherProfile>());
  }

  updateTeacherProfile(
    schoolId: number,
    teacherId: number,
    profile: V3TeacherProfile,
  ): Observable<void> {
    return this.http.put<void>(this.url(schoolId, `/${teacherId}/profile`), profile);
  }

  getTeacherCourseProfiles(
    schoolId: number,
    teacherId: number,
  ): Observable<V3CourseTeacherProfile[]> {
    return this.http.get<V3CourseTeacherProfile[]>(
      this.url(schoolId, `/${teacherId}/course-profiles`),
    );
  }

  updateTeacherCourseProfile(
    schoolId: number,
    teacherId: number,
    code: string,
    courseProfile: V3CourseTeacherProfileDetails,
  ): Observable<void> {
    return this.http.put<void>(
      this.url(schoolId, `/${teacherId}/course-profiles/${code}`),
      courseProfile,
    );
  }

  deleteTeacherCourseProfile(
    schoolId: number,
    teacherId: number,
    code: string,
  ): Observable<void> {
    return this.http.delete<void>(
      this.url(schoolId, `/${teacherId}/course-profiles/${code}`),
    );
  }

  changeTeacherStatus(
    schoolId: number,
    teacherId: number,
    status: LearningUnitTeacherStatus,
  ): Observable<void> {
    return this.http.patch<void>(this.url(schoolId, `/${teacherId}/status`), JSON.stringify(status),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      });
  }

  changeTeacherPasswordInstantly(
    schoolId: number,
    teacherId: number,
    request: VerifiedPasswordChangeRequest,
  ): Observable<void> {
    return this.http.patch<void>(
      this.url(schoolId, `/${teacherId}/person/account/password`),
      request,
    );
  }

}
